import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Auth is cookie-based (httpOnly, SameSite=None across origins). We intentionally
// do NOT cache tokens in localStorage / sessionStorage — that would open an XSS
// token-theft surface for what is otherwise a well-scoped session cookie.
//
// ``X-Requested-With: LastBookmark`` is our anti-CSRF proof-of-first-party.
// The backend rejects any state-changing request that carries neither a
// matching first-party Referer nor this header; browsers refuse to attach
// custom headers to simple cross-origin requests without a CORS preflight,
// and our CORS allowlist rejects preflights from untrusted origins — so a
// third-party page cannot forge this header on the victim's behalf.
export const api = axios.create({
  baseURL: `${BACKEND_URL}/api`,
  withCredentials: true,
  headers: {
    "X-Requested-With": "LastBookmark",
  },
});

// Global 401 handler: when the session expires (or was never valid) and a
// protected endpoint returns 401, bounce the user to /login instead of showing
// them a red "Not authenticated" toast. Auth-check calls under /auth/ are
// exempt so login/register/me/forgot-password can surface their own errors.
// The redirect carries `?next=<origin>` so we can return the user to where
// they were after they sign back in.
api.interceptors.response.use(
  (resp) => resp,
  (err) => {
    const status = err?.response?.status;
    const url = err?.config?.url || "";
    const isAuthCall = url.startsWith("/auth/") || url.startsWith("auth/");
    if (status === 401 && !isAuthCall && typeof window !== "undefined") {
      const path = window.location.pathname;
      if (path !== "/login" && path !== "/register" && !path.startsWith("/forgot-password")) {
        const next = encodeURIComponent(window.location.pathname + window.location.search + window.location.hash);
        window.location.replace(`/login?next=${next}&reason=expired`);
      }
      // Return a promise that never settles — the page is navigating away, so
      // we don't want the caller's `.catch()` to fire a "Not authenticated"
      // toast in the tiny window before unload.
      return new Promise(() => {});
    }
    return Promise.reject(err);
  }
);

export function formatApiError(err) {
  const detail = err?.response?.data?.detail;
  if (detail == null) return err?.message || "Something went wrong.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail
      .map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e)))
      .filter(Boolean)
      .join(" ");
  // Dict-shaped detail — FastAPI dependencies (email-verified guard etc.)
  // raise HTTPException(detail={code, message, ...}). Prefer the
  // human-readable `message` / `msg` / `detail` fields; fall back to a
  // generic string so the toast NEVER shows "[object Object]".
  if (detail && typeof detail === "object") {
    if (typeof detail.message === "string") return detail.message;
    if (typeof detail.msg === "string") return detail.msg;
    if (typeof detail.detail === "string") return detail.detail;
    if (typeof detail.error === "string") return detail.error;
  }
  return err?.message || "Something went wrong. Please try again.";
}

export const fileUrl = (path) => `${BACKEND_URL}/api/uploads/${path}`;
